'use client';

/**
 * ConditionalAnalytics — consent-gated, *non-GTM* trackers only.
 *
 * GTM itself is loaded unconditionally in <body> via
 * `GoogleTagManagerHead` (so it's visible in View Source for SEO,
 * crawlers, and audit tooling) and gates its tags through Google
 * Consent Mode v2.
 *
 * This component exists for the small set of trackers we run *outside*
 * GTM and that have no Consent Mode integration of their own:
 *   - Microsoft Clarity (session replay) — only loaded after the user
 *     accepts the `analytics` category.
 *   - Standalone GA4 — only fires if `NEXT_PUBLIC_GA4_ID` is set to a
 *     real measurement ID (in our setup GA4 is provisioned inside
 *     GTM, so this is a no-op). Kept for future flexibility.
 */

import { useEffect, useState } from 'react';
import GoogleAnalytics from './analytics/GoogleAnalytics';
import MicrosoftClarity from './analytics/MicrosoftClarity';

export default function ConditionalAnalytics() {
  const [analyticsConsent, setAnalyticsConsent] = useState(false);

  useEffect(() => {
    const checkConsent = () => {
      const cookie = document.cookie
        .split('; ')
        .find((row) => row.startsWith('prostay_cookie_consent='));

      if (!cookie) {
        setAnalyticsConsent(false);
        return;
      }
      try {
        const value = JSON.parse(decodeURIComponent(cookie.split('=')[1]));
        setAnalyticsConsent(
          Array.isArray(value.categories) &&
            value.categories.includes('analytics')
        );
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('[ConditionalAnalytics] error parsing cookie consent:', e);
        }
      }
    };

    checkConsent();

    window.addEventListener('cc:onChange', checkConsent);
    window.addEventListener('cc:onConsent', checkConsent);
    return () => {
      window.removeEventListener('cc:onChange', checkConsent);
      window.removeEventListener('cc:onConsent', checkConsent);
    };
  }, []);

  if (!analyticsConsent) return null;

  return (
    <>
      <GoogleAnalytics />
      <MicrosoftClarity />
    </>
  );
}
